import { useState } from 'react';
import { useUsers, useDeactivateUser, useSendInvite, useInvites, useTenants, useUpdateUser } from '@/hooks/useAdmin';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { UserPlus, Clock, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type { UserRole, UserProfile } from '@/types/models';

const ROLES: UserRole[] = ['admin', 'manager', 'operator', 'auditor', 'intake_staff', 'warehouse_staff', 'processing_tech', 'sales', 'driver'];

export default function UsersPage() {
  const { data: users = [] } = useUsers();
  const { data: invites = [] } = useInvites();
  const { data: tenants = [] } = useTenants();
  const deactivate = useDeactivateUser();
  const sendInvite = useSendInvite();
  const updateUser = useUpdateUser();

  // Invite state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteTenantId, setInviteTenantId] = useState('');
  const [inviteRoles, setInviteRoles] = useState<UserRole[]>(['operator']);

  // Edit user state
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserProfile | null>(null);
  const [editRoles, setEditRoles] = useState<UserRole[]>([]);
  const [editTenantId, setEditTenantId] = useState('');

  const safeUsers = Array.isArray(users) ? users : [];
  const safeInvites = Array.isArray(invites) ? invites : [];
  const safeTenants = Array.isArray(tenants) ? tenants : [];

  const toggleRole = (role: UserRole, selected: UserRole[], setSelected: (r: UserRole[]) => void) => {
    setSelected(
      selected.includes(role) ? selected.filter(r => r !== role) : [...selected, role]
    );
  };

  const handleInvite = () => {
    if (!inviteEmail || inviteRoles.length === 0) return;
    sendInvite.mutate(
      {
        email: inviteEmail,
        role: inviteRoles[0],
        roles: inviteRoles,
        tenant_id: inviteTenantId || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Invite sent');
          setInviteOpen(false);
          setInviteEmail('');
          setInviteRoles(['operator']);
          setInviteTenantId('');
        },
      }
    );
  };

  const openEdit = (user: UserProfile) => {
    setEditUser(user);
    setEditRoles(Array.isArray(user.roles) ? [...user.roles] : []);
    setEditTenantId(user.tenant_id || '');
    setEditOpen(true);
  };

  const handleEditSave = () => {
    if (!editUser) return;
    updateUser.mutate(
      {
        id: editUser.id,
        data: {
          name: editUser.name,
          email: editUser.email,
          roles: editRoles,
          tenant_id: editTenantId || editUser.tenant_id,
        },
      },
      {
        onSuccess: () => {
          toast.success('User updated');
          setEditOpen(false);
          setEditUser(null);
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message || 'Failed to save user';
          toast.error(msg);
        },
      }
    );
  };

  const getTenantName = (tenantId: string) => {
    const t = safeTenants.find(t => t.id === tenantId);
    return t?.name || tenantId;
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="User Management" subtitle="Manage users, roles, and organization assignments">
        <Button className="gap-2" onClick={() => setInviteOpen(true)}>
          <UserPlus className="h-4 w-4" /> Invite User
        </Button>
      </PageHeader>

      {/* Active Users */}
      <Card>
        <CardHeader><CardTitle>Users</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {safeUsers.map(user => {
              const name = typeof user.name === 'string' && user.name.trim() ? user.name : user.email || 'Unknown User';
              const initials = name.split(/\s+/).filter(Boolean).map(n => n[0]).join('').slice(0, 2) || '?';
              const roles = Array.isArray(user.roles) ? user.roles : [];

              return (
                <div key={user.id} className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant={user.is_active ? 'default' : 'secondary'} className="text-[10px]">
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(user)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap pl-12">
                    {user.tenant_id && (
                      <Badge variant="outline" className="text-[10px]">{getTenantName(user.tenant_id)}</Badge>
                    )}
                    {roles.map(r => (
                      <Badge key={r} variant="outline" className="text-[10px]">{r.replace('_', ' ')}</Badge>
                    ))}
                    {user.is_super_admin && (
                      <Badge className="bg-purple-500/15 text-purple-700 text-[10px]">Super Admin</Badge>
                    )}
                    {user.last_login_at && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />{format(new Date(user.last_login_at), 'MMM d')}
                      </span>
                    )}
                    {user.is_active && !user.is_super_admin && (
                      <Button variant="ghost" size="sm" className="text-destructive h-6 px-2 text-xs ml-auto" onClick={() => deactivate.mutate(user.id)}>
                        Deactivate
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {safeInvites.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Pending Invites</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {safeInvites.filter(i => !i.accepted_at).map(inv => (
                <div key={inv.id} className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">Expires {format(new Date(inv.expires_at), 'MMM d, yyyy')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{inv.role}</Badge>
                    {inv.tenant_id && <Badge variant="outline" className="text-[10px]">{getTenantName(inv.tenant_id)}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@company.com" />
            </div>
            <div>
              <label className="text-sm font-medium">Organization</label>
              <Select value={inviteTenantId} onValueChange={setInviteTenantId}>
                <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>
                  {safeTenants.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Roles</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map(role => (
                  <label key={role} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={inviteRoles.includes(role)}
                      onCheckedChange={() => toggleRole(role, inviteRoles, setInviteRoles)}
                    />
                    <span className="capitalize">{role.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={!inviteEmail || inviteRoles.length === 0}>
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User — {editUser?.name || editUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Organization</label>
              <Select value={editTenantId} onValueChange={setEditTenantId}>
                <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>
                  {safeTenants.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Roles</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map(role => (
                  <label key={role} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={editRoles.includes(role)}
                      onCheckedChange={() => toggleRole(role, editRoles, setEditRoles)}
                    />
                    <span className="capitalize">{role.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editRoles.length === 0}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
